import type { ColumnDef } from "@tanstack/react-table";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { PayrollStatementNotificationRecipient } from "@/payroll/pay-breakdown/payroll-statement-notifications/types";
import {
  formatTextNumber,
  getNotificationReasonLabel,
} from "@/payroll/pay-breakdown/payroll-statement-notifications/utils";

/**
 * Where the PayPack goes. The mobile line only appears for a business that texts
 * PayPacks, and then says why a technician won't be texted rather than going blank.
 */
const TextLine = ({
  recipient,
}: {
  recipient: PayrollStatementNotificationRecipient;
}) => {
  // Owners are already marked preview-only on the row; saying it twice is noise.
  if (recipient.sms_ineligible_reason === "owner_preview_only") return null;
  if (recipient.sms_ineligible_reason)
    return (
      <Text variant="caption" tone="muted">
        {getNotificationReasonLabel(recipient.sms_ineligible_reason)}
      </Text>
    );
  if (!recipient.phone_number) return null;

  return (
    <Text variant="caption" tone="muted">
      {formatTextNumber(recipient.phone_number)}
    </Text>
  );
};

export const ContactCell = ({
  recipient,
}: {
  recipient: PayrollStatementNotificationRecipient;
}) => (
  <Stack align="start" gap={1} className="w-full min-w-0">
    {recipient.email ? (
      <Text
        variant="caption"
        className="w-full truncate"
        title={recipient.email}
      >
        {recipient.email}
      </Text>
    ) : (
      <Text variant="caption" tone="muted">
        No saved email
      </Text>
    )}
    <TextLine recipient={recipient} />
  </Stack>
);

export const EmailCol = (): ColumnDef<
  PayrollStatementNotificationRecipient,
  unknown
> => ({
  id: "email",
  enableSorting: false,
  header: () => <span>Email</span>,
  cell: ({ row }) => <ContactCell recipient={row.original} />,
});
